import React, { useState, useMemo } from 'react';
import { Button } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
// Api
import { DOWNLOAD_CAMPAIGN_OFFERS } from 'api/campaignV2/mutations';
import { GET_CAMPAIGNS_V2 } from 'api/campaignV2/queries';
// Types
import {
  GetCampaignsV2,
  GetCampaignsV2Variables,
  GetCampaignsV2_getCampaigns_entities,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  DownloadCampaignOffersFull,
  DownloadCampaignOffersFullVariables,
} from 'api/campaignV2/types/DownloadCampaignOffersFull';
import { CampaignStatus, CampaignType } from 'api/graphql-global-types';
// Helpers
import {
  DIGITAL_AGREEMENT_CAMPAIGN_TYPES,
  isCustomLikeCampaign,
  requiresAgreementSignatory,
} from 'helpers/campaign';
import { getContentEditableActivity } from 'helpers/cbActivity';
// Ui
import { successNotification, errorNotification } from 'ui/Notification';
import { DetailCard } from 'ui/DetailList/DetailList';
// Components
import TalentTable from './components/TalentTable/TalentTable';
import CampaignRecordData from './components/CampaignRecordData/CampaignRecordData';
import CbCampaignFundingActions from './components/CbCampaignFundingActions/CbCampaignFundingActions';
import ChangeCampaignMaxBudget from './components/ChangeCampaignMaxBudget/ChangeCampaignMaxBudget';
import ChangePlatformMargin from './components/ChangePlatformMargin/ChangePlatformMargin';
import CampaignActivityData from './components/CampaignActivityData/CampaignActivityData';
import ChangeInviteProcessingFee from './components/ChangeInviteProcessingFee/ChangeInviteProcessingFee';
import ChangeAgreementSignatory from './components/ChangeAgreementSignatory/ChangeAgreementSignatory';
import ChangeRequiresAgreement from './components/ChangeRequiresAgreement/ChangeRequiresAgreement';
import CollaboratorModal from './components/CollaboratorModal/CollaboratorModal';
import TalentInviteLinksModal from './components/TalentInviteLinksModal/TalentInviteLinksModal';
import SocialBoostDisputeResolutionComponent from './components/SocialBoostDisputeResolution/SocialBoostDisputeResolution';
import SocialBoostTalentOffersComponent from './components/SocialBoostTalentOffers/SocialBoostTalentOffers';
import EditCampaignContentModal from './components/EditCampaignContentModal/EditCampaignContentModal';
// Style
import styles from './CampaignDetails.module.scss';

const CampaignDetailsComponent = (): JSX.Element | null => {
  const { campaignId } = useParams<{ campaignId: string | undefined }>();
  const [isCollaboratorsModalVisible, setIsCollaboratorsModalVisible] =
    useState<boolean>(false);
  const [isInviteLinksModalVisible, setIsInviteLinksModalVisible] =
    useState<boolean>(false);
  const [isEditContentModalVisible, setIsEditContentModalVisible] =
    useState<boolean>(false);
  const { data, refetch } = useQuery<GetCampaignsV2, GetCampaignsV2Variables>(
    GET_CAMPAIGNS_V2,
    {
      variables: {
        input: {
          campaignId,
        },
      },
      skip: !campaignId,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  const entities = data?.getCampaigns.entities;
  const record: GetCampaignsV2_getCampaigns_entities | null = useMemo(() => {
    return entities?.[0] || null;
  }, [entities]);

  const [downloadCSV, { loading: downloadingCSV }] = useMutation<
    DownloadCampaignOffersFull,
    DownloadCampaignOffersFullVariables
  >(DOWNLOAD_CAMPAIGN_OFFERS);

  const onCSVButtonClick = async () => {
    try {
      const { data } = await downloadCSV({
        variables: {
          input: {
            campaignId: record?.id || '',
          },
        },
      });

      if (data?.downloadCampaignOffersFull.csvUrl) {
        window.open(data.downloadCampaignOffersFull.csvUrl, '_blank');
        successNotification('CSV has been downloaded');
      }
    } catch (error) {
      console.error('Error downloading CSV', error);
      errorNotification('Error downloading CSV');
    }
  };

  const onCollaboratorsButtonClick = () => {
    setIsCollaboratorsModalVisible(true);
  };

  const showMaxBudgetField =
    !isCustomLikeCampaign(record?.type) &&
    record?.status !== CampaignStatus.COMPLETED &&
    record?.status !== CampaignStatus.DECLINED;

  const isPlatformMarginDisabled = ![
    CampaignStatus.DETAIL_FILLED,
    CampaignStatus.ON_REVIEW,
  ].includes(record?.status as CampaignStatus);

  if (!record) {
    return null;
  }

  const contentEditableActivity = getContentEditableActivity(record);

  return (
    <>
      <div className={styles.root}>
        <CampaignRecordData record={record} />

        <CampaignActivityData
          record={record}
          briefActions={
            contentEditableActivity && (
              <Button onClick={() => setIsEditContentModalVisible(true)}>
                Edit content
              </Button>
            )
          }
        />

        <DetailCard title="Funding and fees">
          <div className={styles.controlGrid}>
            {record?.id && (
              <CbCampaignFundingActions
                campaignId={record.id}
                funding={record.funding}
                refetch={refetch}
              />
            )}

            {showMaxBudgetField && (
              <ChangeCampaignMaxBudget record={record} refetch={refetch} />
            )}

            <ChangePlatformMargin
              record={record}
              refetch={refetch}
              disabled={isPlatformMarginDisabled}
            />

            <ChangeInviteProcessingFee record={record} refetch={refetch} />
          </div>
        </DetailCard>

        {DIGITAL_AGREEMENT_CAMPAIGN_TYPES.includes(record.type) && (
          <DetailCard title="Digital agreement">
            <div className={styles.agreementStack}>
              <ChangeRequiresAgreement record={record} refetch={refetch} />
              {requiresAgreementSignatory(record) && (
                <ChangeAgreementSignatory record={record} refetch={refetch} />
              )}
            </div>
          </DetailCard>
        )}

        <DetailCard title="Access">
          <div className={styles.accessGrid}>
            <div className={styles.accessItem}>
              <p className={styles.accessLabel}>Collaborators</p>
              <p className={styles.accessHint}>
                People on the brand side who can see and act on this campaign.
              </p>
              <Button type="primary" onClick={onCollaboratorsButtonClick}>
                Manage collaborators
              </Button>
            </div>

            <div className={styles.accessItem}>
              <p className={styles.accessLabel}>Talent invite links</p>
              <p className={styles.accessHint}>
                Links that let specific talent join without applying.
              </p>
              <Button
                type="primary"
                onClick={() => setIsInviteLinksModalVisible(true)}
              >
                Manage invite links
              </Button>
            </div>
          </div>
        </DetailCard>

        {record.type === CampaignType.SocialBoost && (
          <>
            <SocialBoostTalentOffersComponent
              campaignId={record.id}
              campaignStatus={record.status}
              refetchCampaign={refetch}
            />
            <SocialBoostDisputeResolutionComponent campaignId={record.id} />
          </>
        )}

        <DetailCard
          title="Talent"
          actions={
            <Button
              loading={downloadingCSV}
              onClick={onCSVButtonClick}
              aria-label="Export talent to CSV"
            >
              Export to CSV
            </Button>
          }
        >
          <TalentTable record={record} />
        </DetailCard>
      </div>
      <CollaboratorModal
        show={isCollaboratorsModalVisible}
        handleClose={() => setIsCollaboratorsModalVisible(false)}
        record={record}
        refetch={refetch}
      />
      <TalentInviteLinksModal
        show={isInviteLinksModalVisible}
        handleClose={() => setIsInviteLinksModalVisible(false)}
        campaignId={record.id}
        agencyId={record.agency.id}
        campaignProcessingFee={(record as any).inviteProcessingFee}
      />
      {contentEditableActivity && (
        <EditCampaignContentModal
          show={isEditContentModalVisible}
          handleClose={() => setIsEditContentModalVisible(false)}
          campaignId={record.id}
          activity={contentEditableActivity}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default CampaignDetailsComponent;
